"use client";

import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Ban, Home } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { Card, CardContent } from "@/components/ui/card.tsx";
import { Button } from "@/components/ui/button.tsx";
import PATHS from "@/routes/paths.ts";

export default function WorkspaceForbidden() {
  const { t } = useTranslation("workspaces");
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoToWorkspaces = () => {
    navigate(`${PATHS.STUDENT.WORKSPACES.WORKSPACES}/${workspaceId}`);
  };

  return (
    <div className="bg-background flex h-[calc(100dvh_-_4rem)] items-center justify-center p-4">
      <Card className="bg-foreground/50 relative mx-auto w-full max-w-xl overflow-hidden rounded-lg border-none p-20 backdrop-blur-xl">
        <div className="absolute inset-0 opacity-35">
          <div className="absolute -top-40 -left-40 size-80 rounded-full bg-gradient-to-br from-red-600 to-red-800" />
          <div className="absolute -right-30 -bottom-30 size-60 rounded-full bg-gradient-to-tr from-red-500 to-red-600" />
        </div>
        <CardContent className="relative z-50 flex flex-col items-center p-8 text-center">
          <motion.div
            animate={{ scale: 1 }}
            className="bg-primary/10 mb-6 flex h-24 w-24 items-center justify-center rounded-full"
            initial={{ scale: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Ban className="h-12 w-12 text-red-600" />
          </motion.div>

          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className="space-y-3">
              <h1 className="text-accent-foreground text-3xl font-bold">
                {t("workspaceForbidden.title", "Access Denied")}
              </h1>
              <p className="text-muted-foreground">
                {t(
                  "workspaceForbidden.description",
                  "You no longer have access to this workspace because you have left it.",
                )}
              </p>
            </div>

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="flex w-full flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <Button className="flex-1" variant="outline" onClick={handleGoBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("workspaceForbidden.goBack", "Go Back")}
              </Button>
              <Button className="flex-1" onClick={handleGoToWorkspaces}>
                <Home className="mr-2 h-4 w-4" />
                {t("workspaceForbidden.goToWorkspaces", "Go to Workspace")}
              </Button>
            </motion.div>
          </motion.div>
        </CardContent>
      </Card>
    </div>
  );
}
